import type { FC } from "react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { DropdownButton, Spacer, Typography } from "@aviary";
import { AppleSSOButton, EmbeddedVideo, GoogleSSOButton, UserAvatar } from "@shared/components";
import { logGaSignupEvent } from "@shared/helpers";
import { hasErrors } from "@shared/helpers/hasErrors";
import { useBreakpoints, useFlippers, useSharedGlobalConfig } from "@shared/hooks";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { NewSSOErrorBox } from "@unauthenticated/shared/components/NewSSOErrorBox/NewSSOErrorBox";
import type { SubmitValues } from "@unauthenticated/shared/components/PractitionerSignUpForm";
import { usePractitionerSignUpMutation } from "@unauthenticated/shared/data/mutations";
import type { PractitionerSignUpMutationData } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation";
import type { PractitionerType } from "@unauthenticated/shared/data/types";
import { useLogPractitionerSignUp, usePractitionerType } from "@unauthenticated/shared/hooks";
import { l } from "@unauthenticated/shared/locales/i18n";

import { DesignationDropdown } from "../DesignationDropdown";
import { DesignationTooltip } from "../DesignationTooltip";
import { FormWrapper } from "../FormWrapper";
import { PractitionerSignUpForm } from "../PractitionerSignUpForm";
import { SignUpCarouselOrFooter } from "../SignUpCarouselOrFooter";
import { SignUpTitle } from "../SignUpTitle";

import { ContinueWithButtons } from "./ContinueWithButtons";
import { EmersonEcologicsMessage } from "./EmersonEcologicsMessage";
import { ImAPatientButton } from "./ImAPatientButton";
import { WellevateMessage } from "./WellevateMessage";

import * as styles from "./PractitionerSignUp.styles";

interface Props {
  practitionerTypes: PractitionerType[];
  additionalAttributes?: PractitionerAdditionalAttributesType;
  csuid: string;
  pageVersion: "prac_by_default_with_sso" | "prac_by_default" | "oauth_prac";
  onCompleted: (data: PractitionerSignUpMutationData) => void;
  isOAuth?: boolean;
  newSSOErrors: string;
  onSignInRedirect?: () => void;
}

const PractitionerSignUp: FC<Props> = ({
  practitionerTypes,
  additionalAttributes,
  csuid,
  pageVersion,
  onCompleted,
  isOAuth,
  newSSOErrors,
  onSignInRedirect,
}) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const pracType = usePractitionerType();
  const { isFromWellevate, isFromEmersonEcologics } = useSharedGlobalConfig();
  const signUpPage = isOAuth ? "oauth_sign_up_page" : "practitioner_sign_up_page";
  const isPeerReferral = !!additionalAttributes?.peerReferralDisplayAttributes;
  const [isAppleSSO] = useFlippers("apple_sso");

  const designationFromURL = practitionerTypes?.find(
    practitionerType => practitionerType.code === pracType
  );

  const [isFormVisible, setIsFormVisible] = useState(!!pracType || isPeerReferral);
  const [designationId, setDesignationId] = useState(designationFromURL?.id || "");

  const practitioner = practitionerTypes.find(
    practitionerType => practitionerType.id === designationId
  );

  const [signUp, { loading, data }] = usePractitionerSignUpMutation({
    onCompleted: payload => {
      if (!hasErrors(payload?.auth?.practitionerSignUp?.errors)) {
        const { id, storeId } = payload?.auth?.practitionerSignUp?.practitioner || {};
        logGaSignupEvent(id, storeId);
      }
      onCompleted(payload);
    },
  });

  const { logField } = useLogPractitionerSignUp({
    eventAggregateId: csuid,
    pageVersion,
    signUpPage,
    apiClientId: additionalAttributes?.apiClientId,
  });

  const handleSubmit = (values: SubmitValues) => {
    const { gToken, ...attributes } = values;

    delete additionalAttributes.peerReferralDisplayAttributes;

    signUp({
      variables: {
        input: {
          attributes: { ...attributes, optional: additionalAttributes },
          captchaToken: gToken,
        },
      },
    });
  };

  const getTriggerText = () => {
    const designation = practitionerTypes.find(type => type.id === designationId);

    return designation?.description || t(l.common.IAmA);
  };

  const renderContinueWithButtonsOrForm = () => {
    if (isFormVisible) {
      return (
        <>
          <div css={styles.fullWidth}>
            <GoogleSSOButton
              pracTypeId={practitioner?.id}
              css={styles.continueWithButton}
              disabled={isPeerReferral && !designationId}
              pageVersion={pageVersion}
              signUpPage={signUpPage}
              additionalAttributes={additionalAttributes}
              isOrTextRendered={!isAppleSSO}
            />
            {isAppleSSO && (
              <AppleSSOButton
                pracTypeId={practitioner?.id}
                css={styles.continueWithButton}
                isOrTextRendered
                disabled={isPeerReferral && !designationId}
                pageVersion={pageVersion}
                signUpPage={signUpPage}
                additionalAttributes={additionalAttributes}
              />
            )}
          </div>

          <PractitionerSignUpForm
            isPeerReferral={isPeerReferral}
            onSubmit={handleSubmit}
            errors={data?.auth?.practitionerSignUp?.errors?.fields}
            isLoading={loading}
            pageVersion={pageVersion}
            requireDesignation
            designationId={designationId}
            setDesignationId={setDesignationId}
            signUpPage={signUpPage}
            logField={logField}
            onSignInRedirect={onSignInRedirect}
            initialEmail={additionalAttributes?.peerReferralDisplayAttributes?.referredEmail ?? ""}
            initialFirstName={
              additionalAttributes?.peerReferralDisplayAttributes?.referredFirstName ?? ""
            }
            initialLastName={
              additionalAttributes?.peerReferralDisplayAttributes?.referredLastName ?? ""
            }
          />
        </>
      );
    }

    return (
      <ContinueWithButtons
        pracTypeId={practitioner?.id}
        setIsFormVisible={setIsFormVisible}
        isActive={!!designationId}
        pageVersion={pageVersion}
        additionalAttributes={additionalAttributes}
        isOAuth
      />
    );
  };

  const renderPeerReferralTitle = () => {
    if (isPeerReferral) {
      const { referrerFirstName, referrerAvatar } =
        additionalAttributes?.peerReferralDisplayAttributes;

      return (
        <div css={styles.peerReferralTitle}>
          {referrerAvatar && (
            <UserAvatar
              size="small"
              avatarImage={referrerAvatar}
              css={styles.userAvatar}
              label={`${referrerFirstName} avatar`}
            />
          )}
          <Typography type="h3">
            {t(l.practitionerSignUp.peerReferral.signUpTitle, { referrerFirstName })}
          </Typography>
        </div>
      );
    }
  };

  const renderTitleSection = () => {
    if (isPeerReferral) return null;

    return (
      <>
        {!isOAuth && <ImAPatientButton />}
        <SignUpTitle user={t(l.practitionerSignUp.Practitioner)} />
      </>
    );
  };

  const renderCompanyMessageByFullscript = () => {
    if (isFromWellevate) return <WellevateMessage />;
    if (isFromEmersonEcologics) return <EmersonEcologicsMessage />;

    return null;
  };

  const renderFooter = () => {
    if (isPeerReferral) {
      return (
        <EmbeddedVideo
          src="https://www.youtube.com/embed/RH9goQv6qmE"
          title={l.practitionerSignUp.peerReferral.demoVideoModalTitle}
        />
      );
    }

    return (
      <SignUpCarouselOrFooter
        isWithinViewport={tablet.greaterThan}
        onSignInRedirect={onSignInRedirect}
      />
    );
  };

  return (
    <>
      {renderPeerReferralTitle()}
      <FormWrapper data-e2e="prac_by_default">
        <div css={styles.textAlign}>
          {renderTitleSection()}

          {!isFromEmersonEcologics && (
            <Typography css={styles.subTitle}>
              <Trans i18nKey={l.practitionerSignUp.LetsGetStarted}>
                Let's get you started! All accounts are 100%{" "}
                <strong css={styles.freeText}>free</strong>
              </Trans>
            </Typography>
          )}

          {renderCompanyMessageByFullscript()}

          {!isFormVisible && <Spacer height="doubleHalf" />}

          <NewSSOErrorBox newSSOErrors={newSSOErrors} onRedirect={onSignInRedirect} />

          <div css={styles.designationRow}>
            <DesignationDropdown practitionerTypes={practitionerTypes} setValue={setDesignationId}>
              <DropdownButton isFullWidth isCentered data-e2e="practitioner-type-trigger">
                {getTriggerText()}
              </DropdownButton>
            </DesignationDropdown>
            <DesignationTooltip />
          </div>

          {renderContinueWithButtonsOrForm()}
        </div>

        <div css={[styles.signUpFooterSection, styles.textAlign]}>{renderFooter()}</div>
      </FormWrapper>
    </>
  );
};

export { PractitionerSignUp };
